import { useMemo } from 'react'

import { CircleQuestionMark } from 'lucide-react'

import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { CapitalGrowthChart } from '@/pages/funds/ic-report/components/primitives/charts/CapitalGrowthChart.new'
import { CapitalPreservationChart } from '@/pages/funds/ic-report/components/primitives/charts/CapitalPreservationChart.new'
import { labels } from '@/pages/funds/ic-report/constants'
import { beta } from '@/react-query/queries/ic-report/hooks'

export interface CapitalBarriersProps {
  params: Omit<ICReport.GetICReportDataParams, 'endpoint'>
}

export const CapitalBarriers: React.FC<CapitalBarriersProps> = ({ params }) => {
  const growthBarriersQuery = beta.useCapitalGrowthBarriers(params)
  const growthData = useMemo(() => growthBarriersQuery.data ?? [], [growthBarriersQuery.data])
  const growthAvg =
    growthData.reduce((prev, curr) => prev + curr.barrier_level, 0) / growthData.length

  const preservationBarriersQuery = beta.useCapitalProtectionBarriers(params)
  const protectionData = useMemo(
    () => preservationBarriersQuery.data ?? [],
    [preservationBarriersQuery.data],
  )
  const protectionAvg =
    protectionData.reduce((prev, curr) => prev + curr.barrier_level, 0) / protectionData.length

  const preservationLabels = labels.beta.cover_to_capital_preservation().descriptions
  const growthLabels = labels.beta.cover_to_capital_growth().descriptions

  return (
    <div className={'grid grid-cols-2 gap-4'}>
      <Widget>
        <WidgetHeaderWithQuery
          title={`Cover to Capital Preservation | ${(growthAvg * 100).toFixed(2)}% Avg`}
          query={preservationBarriersQuery}
        />
        <WidgetContent loading={preservationBarriersQuery.isLoading}>
          {preservationLabels?.widget && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm border-b bg-sidebar text-muted-foreground">
              <CircleQuestionMark size={16} className={'text-blue-600 shrink-0'} />
              {preservationLabels.widget}
            </div>
          )}
          <CapitalPreservationChart params={params} />
        </WidgetContent>
      </Widget>
      <Widget>
        <WidgetHeaderWithQuery
          title={`Cover to Capital Growth | ${(protectionAvg * 100).toFixed(2)}% Avg`}
          query={growthBarriersQuery}
        />
        <WidgetContent loading={growthBarriersQuery.isLoading}>
          {growthLabels?.widget && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm border-b bg-sidebar text-muted-foreground">
              <CircleQuestionMark size={16} className={'text-blue-600 shrink-0'} />
              {growthLabels.widget}
            </div>
          )}
          <CapitalGrowthChart params={params} />
        </WidgetContent>
      </Widget>
    </div>
  )
}
